import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import cn from 'classnames';
import BottomDrawer from '../../../../../../../../components/BottomDrawer/BottomDrawer';
import IndicatorBadge from '../../../../../../components/common/IndicatorBadge/IndicatorBadge';
import { addressStagesColorsMap } from '../../../../../../constants/stagesColorMaps';
import { total } from '../../utils/total';
import {
  acceptanceResultsStatuses,
  flatStatusToAcceptanceResult,
} from '../../../../../../constants/common';
import { links } from '../../../../../../constants/links';
import { columnKeyMap } from '../../../../../../components/ui/tables/constants/columnKeyMap';
import styles from './HouseStagesDrawer.module.scss';

const drawerBodyStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
};

const headRowData = [
  { id: 'status', name: 'Статус' },
  { id: 'total', name: 'Всего' },
];

const bodyRowData = [{ id: 'total', name: 'Итого' }];

const generateLink = (pathname, stageName, acceptanceResultName) => {
  const path = pathname
    .split('/')
    .slice(0, -1)
    .concat(links.house.pages.flats)
    .join('/');

  return {
    pathname: path,
    state: {
      [columnKeyMap.addressStageName]: stageName,
      [columnKeyMap.flatAcceptanceResult]: acceptanceResultName,
    },
  };
};

const drawerAcceptanceResultContent = ({
  stage,
  result,
  acceptanceResultsMap,
  pathname,
}) => {
  const relatedAcceptanceResults = flatStatusToAcceptanceResult.get(stage.id);
  const stageIncludesResult = relatedAcceptanceResults?.includes(result.id);
  const acceptanceResultCount = acceptanceResultsMap.get(result.id)?.count;
  const isTotal = stage.id === 'total';

  const noContent = !stageIncludesResult && !isTotal;
  const pContent = isTotal || !acceptanceResultCount;
  const linkContent = !isTotal && Boolean(acceptanceResultCount);

  if (noContent) {
    return <p className={styles.text}>-</p>;
  }

  if (pContent) {
    return <p className={styles.text}>{acceptanceResultCount || 0}</p>;
  }

  if (linkContent) {
    return (
      <Link
        className={styles.text}
        style={{ width: '100%', display: 'block' }}
        to={generateLink(pathname, stage.name, result.name)}
      >
        {acceptanceResultsMap.get(result.id)?.count}
      </Link>
    );
  }
};

const HouseStagesDrawer = ({
  visible,
  onClose,
  acceptanceResults,
  flatStages,
  flatStagesMap,
  acceptanceResultsMap,
}) => {
  const { pathname } = useLocation();

  const [results, setResults] = useState(acceptanceResults);

  const drawerHeadLength = acceptanceResults.length + 1;
  const gridTemplateColumnsStyle = {
    gridTemplateColumns: `220px repeat(${drawerHeadLength}, calc((100% - 220px)/${drawerHeadLength})`,
  };

  const headData = headRowData.concat(results);
  const bodyData = flatStages.sort((a, b) => a.id - b.id).concat(bodyRowData);

  useEffect(() => {
    let sortResults = acceptanceResults.sort((a, b) => a.id - b.id);

    const notAcceptedI = sortResults.findIndex(
      (item) => item.id === acceptanceResultsStatuses.notAccepted
    );

    const acceptedI = sortResults.findIndex(
      (item) => item.id === acceptanceResultsStatuses.accepted
    );

    [sortResults[notAcceptedI], sortResults[acceptedI]] = [
      sortResults[acceptedI],
      sortResults[notAcceptedI],
    ];

    setResults(sortResults);
  }, [acceptanceResults]);

  return (
    <BottomDrawer
      visible={visible}
      onClose={onClose}
      bodyStyle={drawerBodyStyle}
    >
      <div
        className={styles.head}
        style={gridTemplateColumnsStyle}
      >
        {headData.map((item) => (
          <div
            key={item.id}
            className={styles.cell}
          >
            <p className={cn(styles.text, styles.accent)}>{item.name}</p>
          </div>
        ))}
      </div>
      <div className={styles.body}>
        {bodyData.map((stage) => (
          <div
            key={stage.id}
            className={styles.row}
            style={gridTemplateColumnsStyle}
          >
            <div className={cn(styles.cell, styles.cellFlex)}>
              {addressStagesColorsMap.get(stage.id) && (
                <IndicatorBadge
                  style={{ flexShrink: 0 }}
                  bgColor={addressStagesColorsMap.get(stage.id)}
                />
              )}
              {stage.id === 'total' || !flatStagesMap.get(stage.id)?.count ? (
                <p
                  className={cn(styles.text, {
                    [styles.accent]: stage.id === 'total',
                  })}
                >
                  {stage.name}
                </p>
              ) : (
                <Link
                  className={styles.text}
                  to={generateLink(pathname, stage.name)}
                >
                  {stage.name}
                </Link>
              )}
            </div>
            <div className={styles.cell}>
              {stage.id === 'total' ? (
                <p className={styles.text}>{total(flatStagesMap)}</p>
              ) : Boolean(flatStagesMap.get(stage.id)?.count) ? (
                <Link
                  className={styles.text}
                  style={{ width: '100%', display: 'block' }}
                  to={generateLink(pathname, stage.name)}
                >
                  {flatStagesMap.get(stage.id)?.count}
                </Link>
              ) : (
                <p className={styles.text}>0</p>
              )}
            </div>
            {results.map((result) => (
              <div
                className={styles.cell}
                key={result.id}
              >
                {drawerAcceptanceResultContent({
                  stage,
                  result,
                  acceptanceResultsMap,
                  pathname,
                })}
              </div>
            ))}
          </div>
        ))}
      </div>
    </BottomDrawer>
  );
};

export default HouseStagesDrawer;
